/**
 * Tells the support chat who the signed-in fan is, so it already knows their
 * email and never asks "Where should we reply if you leave?".
 *
 * Mounted once inside `AuthProvider`, which holds its children back while a
 * reload renews an expired access token. So this sees a fresh sign-in, a
 * reload with a live session and every way a session ends, through one
 * `useAuth()`, with no call added to any sign-out button.
 *
 * What it tells the widget, and when:
 *
 *  - identify, with exactly what auth-api signed (POST /support/identity-get),
 *    once per (externalId, email, userHash). The provider makes a network call
 *    per identify, so a rerender, a focus or a token refresh sends nothing.
 *  - reset, when the session really ended (no refresh token left on the
 *    device) and this device had told the widget who the fan was. A
 *    signed-out state that still carries a refresh token is a renewal that
 *    failed for a passing reason (a 429, a 502) and the fan is about to be
 *    signed back in, so it resets nothing. `sessionEndCount` from the provider
 *    is what re-runs this when such a renewal is later refused: `state` stays
 *    signed out throughout and would not.
 *  - reset on a signed-out page load only when the device remembers an
 *    identified fan (the `SUPPORT_IDENTIFIED_KEY` marker below), so a fan whose
 *    session expired while the tab was closed is not brought back for the next
 *    person on the browser. Never otherwise: HRC's reset "forgets the session,
 *    the identity and every profile change, tag, event and session value not
 *    sent yet" (https://hrc.lt/docs/javascript-api), so a reset on every
 *    signed-out load would wipe the anonymous chat of a visitor who wrote to
 *    support from the sign-in screen, and their reply with it.
 *  - reset, then identify, when a different fan signs in, on the same page or
 *    on a later page load over the marker of the one before.
 *  - reset when the identified fan's identity goes away (their email was
 *    removed, so there is nothing left to sign).
 *  - nothing when auth-api answers no identity because the secret is not
 *    configured: the chat stays anonymous, exactly as before.
 *  - nothing on an error answer, which says nothing about who is signed in.
 *
 * After identify, the unsigned extras an agent reads beside the conversation
 * (`supportProfile.ts`): `update` with the name, avatar and attributes, and
 * `setTags`. Built from reads the app makes anyway: the own-profile read
 * (which `OnboardingGate` already loads on every signed-in screen, so this
 * shares its cache entry) and, only when some screen already loaded them, the
 * fan's pools and notification switches; those two are never fetched for the
 * chat. Identify never waits for them. Sent again only when they change, so a
 * refetch with the same answer sends nothing. A cached read older than the
 * current fan (another tab switched accounts and nothing cleared the cache)
 * is left out, and the profile is used only when it is this fan's.
 *
 * Every identify also writes the support identity cookie
 * (`supportIdentityCookie.ts`) with the same identity and extras, so the
 * marketing site's chat knows the fan too. It is cleared with every reset,
 * whenever the session really ends, and whenever there is no identity to
 * send.
 *
 * Inert anywhere but the production host, where the widget never loads: the
 * query is disabled and the window is never touched.
 */
import { useSupportIdentityGet } from "@sportsmet/auth-sdk/private";
import { useNotificationSettingsGetGlobal } from "@sportsmet/notification-sdk/private";
import { usePoolListMine } from "@sportsmet/pool-sdk/private";
import { useProfileGet } from "@sportsmet/profile-sdk/private";
import { type ReactNode, useEffect, useRef } from "react";

import {
  clearSupportIdentityCookie,
  type typeCookieJar,
  writeSupportIdentityCookie,
} from "../supportIdentityCookie";
import { buildSupportProfile } from "../supportProfile";
import {
  identifySupportUser,
  isSupportWidgetHost,
  resetSupportUser,
  setSupportTags,
  updateSupportProfile,
} from "../supportWidget";
import { useAuth } from "./AuthProvider";
import { readRefreshToken } from "./sessionCookies";

/**
 * The fan this browser last identified to the support chat, kept across page
 * loads in `localStorage`. Written with every identify and removed with every
 * reset, so it is present exactly while the widget may still hold an
 * identified session. Every read and write swallows its own failure: storage
 * the browser refuses reads as "nothing identified", which costs only the
 * page-load reset, never an anonymous visitor's chat.
 */
export const SUPPORT_IDENTIFIED_KEY = "sm.support.identifiedUserId";

function readIdentifiedMarker(): string | null {
  try {
    const value = window.localStorage.getItem(SUPPORT_IDENTIFIED_KEY);
    return value === null || value === "" ? null : value;
  } catch {
    return null;
  }
}

function writeIdentifiedMarker(userId: string | null): void {
  try {
    if (userId === null) window.localStorage.removeItem(SUPPORT_IDENTIFIED_KEY);
    else window.localStorage.setItem(SUPPORT_IDENTIFIED_KEY, userId);
  } catch {
    // Nothing to do: the worst case is one reset more or less next load.
  }
}

function hasRefreshToken(): boolean {
  const refreshToken = readRefreshToken();
  return refreshToken !== undefined && refreshToken !== "";
}

type typeLastSent =
  { kind: "reset" } | { kind: "identified"; userId: string; key: string };

export const SupportWidgetIdentity = ({
  hostname = window.location.hostname,
  win = window,
  cookieJar = document,
}: {
  hostname?: string;
  win?: object;
  cookieJar?: typeCookieJar;
}): ReactNode => {
  const { state, sessionEndCount } = useAuth();
  const isHost = isSupportWidgetHost(hostname);
  const userId = state.status === "signedIn" ? state.user.userId : null;

  const identityQuery = useSupportIdentityGet(
    {},
    {
      enabled: isHost && userId !== null,
      // The generated key carries no payload segments, so the user id keeps
      // one fan's signed identity from ever answering for another.
      queryKeyIdentifier: userId ?? "signedOut",
      // The hash only changes with the email, and the email change and
      // removal mutations invalidate this query.
      staleTime: Number.POSITIVE_INFINITY,
      refetchOnWindowFocus: false,
    },
  );
  // An error envelope (a 500, a dropped connection) says nothing about who the
  // fan is, so it leaves whatever the widget was last told alone.
  const isSettled = identityQuery.isSuccess && identityQuery.data.error == null;
  const identity = identityQuery.data?.data ?? null;

  // The same cache entry the shell's onboarding gate loads.
  const profileQuery = useProfileGet(
    {},
    { enabled: isHost && userId !== null },
  );
  // Read from the cache only: never fetched for the chat.
  const poolsQuery = usePoolListMine({}, { enabled: false });
  const notificationsQuery = useNotificationSettingsGetGlobal(
    {},
    { enabled: false },
  );
  const profile = profileQuery.data?.data?.profile ?? null;
  const pools = poolsQuery.data?.data ?? null;
  const poolsAt = poolsQuery.dataUpdatedAt;
  const notifications = notificationsQuery.data?.data ?? null;
  const notificationsAt = notificationsQuery.dataUpdatedAt;

  // What this page last told the widget. Null until it told it anything, and
  // then the marker speaks for whatever an earlier page load told it.
  const lastSent = useRef<typeLastSent | null>(null);
  // The extras last sent and the cookie last written, for the identified fan.
  const lastExtrasKey = useRef<string | null>(null);
  const lastCookieKey = useRef<string | null>(null);
  // When the current fan was first seen, so a cached read fetched for an
  // earlier fan in this tab is not taken for theirs.
  const userSince = useRef<{ userId: string | null; at: number }>({
    userId: null,
    at: 0,
  });

  useEffect(() => {
    if (!isHost) return;
    if (userSince.current.userId !== userId) {
      userSince.current = { userId, at: Date.now() };
    }

    const identifiedUserId = (): string | null => {
      if (lastSent.current === null) return readIdentifiedMarker();
      return lastSent.current.kind === "identified"
        ? lastSent.current.userId
        : null;
    };
    const clearCookie = (): void => {
      clearSupportIdentityCookie({ jar: cookieJar, hostname });
      lastCookieKey.current = null;
    };
    const sendReset = (): void => {
      resetSupportUser(win);
      lastSent.current = { kind: "reset" };
      lastExtrasKey.current = null;
      writeIdentifiedMarker(null);
      clearCookie();
    };

    if (userId === null) {
      if (hasRefreshToken()) return;
      // The session is over: whatever the marketing site was told goes too,
      // even where this device never told the widget anything.
      clearCookie();
      if (identifiedUserId() === null) return;
      sendReset();
      return;
    }

    const previous = identifiedUserId();
    if (previous !== null && previous !== userId) sendReset();
    if (!isSettled) return;

    if (identity === null || identity.externalId !== userId) {
      if (identifiedUserId() !== null) sendReset();
      else clearCookie();
      return;
    }

    const key = `${identity.externalId}\n${identity.email}\n${identity.userHash}`;
    if (
      lastSent.current?.kind !== "identified" ||
      lastSent.current.key !== key
    ) {
      identifySupportUser(win, identity);
      lastSent.current = { kind: "identified", userId, key };
      lastExtrasKey.current = null;
      writeIdentifiedMarker(userId);
    }

    const since = userSince.current.at;
    const extras = buildSupportProfile({
      profile: profile?.accountId === userId ? profile : null,
      pools: poolsAt >= since ? pools : null,
      notifications: notificationsAt >= since ? notifications : null,
    });
    const extrasKey = JSON.stringify(extras);
    if (lastExtrasKey.current !== extrasKey) {
      updateSupportProfile(win, extras.profile);
      setSupportTags(win, extras.tags);
      lastExtrasKey.current = extrasKey;
    }

    const cookieKey = `${key}\n${extrasKey}`;
    if (lastCookieKey.current !== cookieKey) {
      writeSupportIdentityCookie({
        jar: cookieJar,
        hostname,
        identity,
        profile: extras.profile,
        tags: extras.tags,
      });
      lastCookieKey.current = cookieKey;
    }
  }, [
    isHost,
    hostname,
    win,
    cookieJar,
    userId,
    isSettled,
    identity,
    sessionEndCount,
    profile,
    pools,
    poolsAt,
    notifications,
    notificationsAt,
  ]);

  return null;
};
